import { stripHtml } from '~/ui-rocco/pages/detail-shared/utils';

/** The description spread takes at most this many paragraphs. */
const MAX_PARAGRAPHS = 14;

/** A sentence, with its closing punctuation and any quote or bracket after it. */
const SENTENCE = /[^.!?]+[.!?]+["')\]]*\s*|[^.!?]+$/g;

/**
 * The project's introduction and the description under it, as the lookbook
 * and its cover preview both read them.
 *
 * The introduction is the lead statement project detail sets large in About
 * the Project, without the "ArchiPro Project Summary –" some carry. A project
 * without one lends its description's first sentence, which then leaves the
 * description so it is not read twice.
 */
export const lookbookIntro = (
    subheading: string | null | undefined,
    content: string | null | undefined,
): { heading: string; paragraphs: string[] } => {
    const lead = (subheading ?? '')
        .replace(/^\s*ArchiPro Project Summary\s*[-–—:]\s*/i, '')
        .trim();
    // The markup's block ends become breaks, and what is left is text.
    const paragraphs = (content ?? '')
        .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .split('\n')
        .map((line) => stripHtml(line).trim())
        .filter(Boolean)
        .slice(0, MAX_PARAGRAPHS);
    if (lead) return { heading: lead, paragraphs };

    const [opening, ...rest] = (paragraphs[0] ?? '').match(SENTENCE) ?? [];
    if (!opening) return { heading: '', paragraphs };
    const remainder = rest.join('').trim();
    return {
        heading: opening.trim(),
        paragraphs: remainder
            ? [remainder, ...paragraphs.slice(1)]
            : paragraphs.slice(1),
    };
};

/**
 * The cover's paragraph in capitals: the book, the project and who it is by,
 * then the project's introduction when it has one.
 */
export const coverStatement = (
    title: string,
    professional: string,
    heading: string | null | undefined,
): string => {
    const lead = `Project look book featuring ${title} by ${professional}`;
    const stop = /[.!?]$/.test(lead) ? '' : '.';
    return heading ? `${lead}${stop} ${heading}` : `${lead}${stop}`;
};
